import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import loadable from '@loadable/component'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import PropTypes from 'prop-types'
import React from 'react'
import { getHeadingNameFromContent, slugifyHeading } from '../lib/heading'

import { BrandLink } from './BrandLink'
import ExternalLink from './ExternalLink'

const BlogPostInline = loadable(() => import('./BlogPostInline'))
const EcogestureInline = loadable(() => import('./EcogestureInline'))
const OrganizationInline = loadable(() => import('./OrganizationInline'))
const ProductInline = loadable(() => import('./ProductInline'))
const TipsheetInline = loadable(() => import('./TipsheetInline'))
const ProductLargeCard = loadable(() => import('./ProductLargeCard'))

const options = {
  renderNode: {
    [INLINES.HYPERLINK]: node => {
      const uriDomain = new URL(node.data.uri).hostname
      if (uriDomain === 'www.planete-durable.fr') {
        return (
          <a href={node.data.uri} title={node.content[0].value}>
            {node.content[0].value}
          </a>
        )
      }

      return (
        <ExternalLink
          href={node.data.uri}
          title={node.content[0].value}
        >
          {node.content[0].value}
        </ExternalLink>
      )
    },
    [INLINES.ENTRY_HYPERLINK]: node => {
      switch (node.data?.target?.__typename) {
        case 'ContentfulBlogPost':
          const title = node.data.target.title
          return (
            <Link to={`/${node.data.target.category.slug}/${node.data.target.slug}/`} alt={title} title={title}>
              {title}
            </Link>
          )

        default:
          return null
      }
    },
    [INLINES.EMBEDDED_ENTRY]: node => {
      switch (node.data?.target?.__typename) {
        case 'ContentfulBlogPost':
          return <BlogPostInline {...node.data.target} />

        case 'ContentfulBrand':
          return <BrandLink {...node.data.target} />

        case 'ContentfulProduct':
          return <ProductInline {...node.data.target} />

        case 'ContentfulTipsheet':
          return <TipsheetInline {...node.data.target} />

        case 'ContentfulEcogesture':
          return <EcogestureInline {...node.data.target} />

        case 'ContentfulOrganization':
          return <OrganizationInline {...node.data.target} />

        default:
          return null
      }
    },
    [BLOCKS.HEADING_2]: node => {
      const headingName = getHeadingNameFromContent(node)

      return <h2 id={slugifyHeading(headingName)}>{headingName}</h2>
    },
    [BLOCKS.HEADING_3]: node => {
      const headingName = getHeadingNameFromContent(node)

      return <h3 id={slugifyHeading(headingName)}>{headingName}</h3>
    },
    [BLOCKS.HEADING_4]: node => {
      const headingName = getHeadingNameFromContent(node)

      return <h4 id={slugifyHeading(headingName)}>{headingName}</h4>
    },
    [BLOCKS.EMBEDDED_ASSET]: node => {
      switch (node.data?.target?.__typename) {
        case 'ContentfulAsset':
          return (
            <div className="container mx-auto max-w-sm py-4">
              <GatsbyImage
                image={node.data.target.gatsbyImageData}
                className="object-none object-center"
                title={node.data.target.title}
                alt={node.data.target.title} />
            </div>
          );

        default:
          return null
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      switch (node.data?.target?.__typename) {
        case 'ContentfulProduct':
          return <ProductLargeCard {...node.data.target} />

        case 'ContentfulOffer':
          return <ProductLargeCard {...node.data.target} />

        default:
          return null
      }
    },
  },
}

export const renderRichTextWithOptions = content => renderRichText(content, options)

export const RichContent = ({ content, className }) => (
  <div className={className}>{renderRichTextWithOptions(content)}</div>
)

RichContent.propTypes = {
  content: PropTypes.object.isRequired,
  className: PropTypes.string,
}
